import React, { useState, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuction, useCreateLot, useDeleteAuction, useUpdateAuction, useUpdateLot, useFetchHammerPrices } from './hooks/useAuctions';
import { useExchangeRate } from './hooks/useExchangeRate';
import useLotColumns, { useRows } from './hooks/useLotColumns';
import AuctionLotsToolbar from './AuctionLotsToolbar';
import { certificationServices } from '../../utils/api';
import { Dialog, Transition } from '@headlessui/react';
import { TableProvider, TableContext } from '../../components/shared/table/context/TableContext';
import BaseTable, { ROW_HEIGHT } from '../../components/shared/table/BaseTable';
import { formatDate } from '../../utils/dateUtils';
import { useAuth } from '../../context/AuthContext';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// Configure Day.js plugins
dayjs.extend(utc);
dayjs.extend(timezone);
import { BatchLotForm } from './components/BatchLotForm';
import { useBatchLotForm } from './hooks/useBatchLotForm';
import LotFormModal from './components/LotFormModal';
import ImportLotsModal from './components/ImportLotsModal';
import AuctionFormModal from './AuctionFormModal';
import AuctionLotCard from './components/AuctionLotCard';
import { useMediaQuery } from '../../hooks/useMediaQuery';

const TableContent = ({
  auction,
  processedLots,
  isLoading,
  error,
  onAddLots,
  handleUpdateLot,
  navigate,
  onSystemAuctionAddLots
}) => {
  const tableContext = React.useContext(TableContext);
  const isMobile = useMediaQuery('(max-width: 768px)');
  
  // Get sorted data based on current sort config or default to lot number
  const sortedLots = useMemo(() => {
    if (tableContext?.sortConfig?.length === 0) {
      // If no sort is selected, sort by lot number without setting it as active
      return [...processedLots].sort((a, b) => {
        const numA = parseFloat(a.lot_number) || -Infinity;
        const numB = parseFloat(b.lot_number) || -Infinity;
        return numA - numB;
      });
    }
    return processedLots;
  }, [processedLots, tableContext?.sortConfig]);

  const columns = useLotColumns(
    auction?.auction_house_currency,
    auction?.user_currency,
    auction?.profit_margin || 0,
    auction?.buyers_premium || 0,
    handleUpdateLot
  ) || [];

  return (
    <div className="flex flex-col h-full">
      <div className="flex-none">
        <AuctionLotsToolbar
          columns={columns}
          data={sortedLots}
          onAddLots={auction.system_auction_id ? onSystemAuctionAddLots : onAddLots}
        />
      </div>
      <div className="flex-1 min-h-0">
        {isMobile ? (
          <div className="p-4 space-y-4 bg-gray-50 overflow-y-auto">
            {sortedLots.map(lot => (
              <AuctionLotCard
                key={lot.id}
                lot={lot}
                auctionId={auction.id}
              />
            ))}
          </div>
        ) : (
          <BaseTable
            data={sortedLots}
            columns={columns}
            loading={isLoading}
            error={error}
            onRowClick={(id, button, target) => {
              // Don't navigate if clicking an element with data-no-row-click attribute
              const shouldSkipNavigation = target?.closest('[data-no-row-click]');
              if (!shouldSkipNavigation) {
                const lot = auction.lots.find(lot => lot.id === id);
                navigate(`/auctions/${auction.id}/lots/${lot.lot_number}`);
              }
            }}
            className="cursor-pointer"
          />
        )}
      </div>
    </div>
  );
};

const formatCurrency = (amount, currency = 'USD') => {
  if (amount === null || amount === undefined) return '-';
  // For JPY, round to whole number
  if (currency === 'JPY') {
    amount = Math.round(amount);
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: currency === 'JPY' ? 0 : 2
  }).format(amount);
};

function ExchangeRateDisplay({ fromCurrency, toCurrency, inline }) {
  // Get rate from user currency to house currency
  const { data: rate, isLoading, error } = useExchangeRate(toCurrency, fromCurrency);
  
  if (isLoading) {
    return inline ? "Loading exchange rate..." : <p className="text-sm text-gray-500">Loading exchange rate...</p>;
  }
  
  if (error) {
    return inline ? "Failed to load exchange rate" : <p className="text-sm text-red-500">Failed to load exchange rate</p>;
  }
  
  if (!rate || fromCurrency === toCurrency) {
    return null;
  }
  
  const text = `Exchange Rate: 1 ${toCurrency} = ${rate.toFixed(4)} ${fromCurrency}`;
  return inline ? text : <p className="text-sm text-gray-500">{text}</p>;
}

export default function AuctionDetailsPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const { data: auction, isLoading, error } = useAuction(id);
  const { data: rate } = useExchangeRate(auction?.auction_house_currency, auction?.user_currency);
  const { mutate: createLot, isLoading: isCreatingLot } = useCreateLot();
  const [isAddLotOpen, setIsAddLotOpen] = useState(false);
  const [selectedLot, setSelectedLot] = useState(null);
  const [isEditLotOpen, setIsEditLotOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isImportLotsOpen, setIsImportLotsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const { mutate: fetchHammerPrices, isLoading: isFetchingHammerPrices } = useFetchHammerPrices();
  const [isHammerPriceSuccessOpen, setIsHammerPriceSuccessOpen] = useState(false);
  const [hammerPriceUpdateCount, setHammerPriceUpdateCount] = useState(0);

  // Helper function to generate array of dates between start and end date
  const getDatesInRange = (startDate, endDate) => {
    const dates = [];
    const start = new Date(startDate).toISOString();
    const end = endDate ? new Date(endDate).toISOString() : new Date(startDate).toISOString();
    
    for (let date = start; date <= end; date.setDate(date.getDate() + 1)) {
      dates.push(new Date(date).toISOString());
    }
    
    return dates;
  };
  const { mutate: updateAuction } = useUpdateAuction();
  const { mutate: updateLot } = useUpdateLot();
  const queryClient = useQueryClient();

  const handleUpdateLot = (params) => {
    // Find the existing lot to get all its current values
    const existingLot = auction.lots.find(lot => lot.id === params.id);
    if (!existingLot) return;

    // Merge the existing values with the update to ensure updates override existing values
    const updatedLot = {
      ...params,
      ...existingLot,
      // Explicitly set the fields from params to ensure they override
      ...Object.keys(params).reduce((acc, key) => ({
        ...acc,
        [key]: params[key]
      }), {})
    };

    // Optimistically update the UI
    queryClient.setQueryData(['auctions', id], (oldData) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        lots: oldData.lots.map(lot => 
          lot.id === params.id ? { ...lot, ...params } : lot
        )
      };
    });

    return new Promise((resolve, reject) => {
      updateLot({
        auctionId: id,
        lotId: params.id,
        lot: updatedLot
      }, {
        onSuccess: () => {
          queryClient.invalidateQueries(['auctions']);
          resolve();
        },
        onError: (error) => {
          // Revert optimistic update on error
          queryClient.invalidateQueries(['auctions']);
          reject(error);
        }
      });
    });
  };

  const [services, setServices] = useState([]);
  const batchForm = useBatchLotForm(services);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const servicesData = await certificationServices.getAll();
        setServices(servicesData);
      } catch (error) {
        console.error('Failed to fetch certification services:', error);
      }
    };
    fetchServices();
  }, []);
  const handleSystemAuctionAddLots = () => {
    if (auction?.system_auction_id) {
      navigate('/auctions', { 
        state: { 
          selectedSystemAuctionId: auction.system_auction_id,
          selectedTab: 1 // Index 1 is the "Browse Auctions" tab
        }
      });
    }
  };
  const { mutate: deleteAuction, isLoading: isDeleting } = useDeleteAuction();

  const [isDeleteBlockedOpen, setIsDeleteBlockedOpen] = useState(false);

  const handleDelete = async () => {
    // Check if auction has won or imported lots
    const hasWonOrImportedLots = auction.lots?.some(lot => 
      ['won', 'imported'].includes(lot.status)
    );

    if (hasWonOrImportedLots) {
      setIsDeleteBlockedOpen(true);
      return;
    }

    try {
      await deleteAuction(id);
      navigate('/auctions');
    } catch (error) {
      console.error('Failed to delete auction:', error);
    }
  };

  const [formError, setFormError] = useState(null);
  const processedLots = useRows(auction?.lots || []);

  const handleSubmit = async () => {
    setFormError(null);
    const validRows = batchForm.rows.filter(row => 
      row.lot_number && row.country && row.year && row.denomination
    );

    if (validRows.length === 0) {
      setFormError('Please fill in all required fields (Lot #, Country, Year, Denomination)');
      return;
    }

    try {
      // Create lots sequentially to maintain order
      for (const row of validRows) {
        const cleanRow = batchForm.cleanRowData(row);
        try {
          await createLot(
            { auctionId: id, lot: cleanRow },
            {
              onError: (err) => {
                console.error('Server validation errors:', err?.response?.data); // Log detailed error
                throw err?.response?.data?.errors || err;
              }
            }
          );
        } catch (err) {
          console.error('Detailed error:', err); // Log the full error object
          throw err;
        }
      }

      setIsAddLotOpen(false);
    } catch (err) {
      console.error('Failed to create lot:', err);
      // Show more detailed error message
      setFormError(
        Array.isArray(err) 
          ? err.map(e => e.msg).join(', ') 
          : err?.response?.data?.error || err?.message || 'Failed to create lot'
      );
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const errorMessage = error?.message || (error instanceof Error ? error.toString() : 'An error occurred');

  if (error) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="text-red-600 bg-red-50 px-4 py-3 rounded-lg">
          Failed to load auction: {errorMessage}
        </div>
      </div>
    );
  }

  if (!auction) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="text-gray-600">Auction not found</div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col p-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-4 sm:px-6 py-3 sm:py-5">
          <div className="flex-1">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">{auction.name}</h2>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              {auction.source_url ? (
                <a 
                  href={auction.source_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:text-indigo-500"
                >
                  {auction.auction_house_name}
                </a>
              ) : (
                auction.auction_house_name
              )} • {formatDate(auction.start_date, { user })}{auction.end_date ? ` - ${formatDate(auction.end_date, { user })}` : ''}
            </p>
            <div className="mt-2">
              <ExchangeRateDisplay 
                fromCurrency={auction.user_currency} 
                toCurrency={auction.auction_house_currency} 
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-3 sm:mt-0">
            {auction.system_auction_id && (
              <button
                type="button"
                onClick={() => {
                  fetchHammerPrices(auction.id, {
                    onSuccess: (data) => {
                      setHammerPriceUpdateCount(data.updatedCount);
                      setIsHammerPriceSuccessOpen(true);
                    }
                  });
                }}
                disabled={isFetchingHammerPrices}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {isFetchingHammerPrices ? 'Fetching...' : 'Fetch Prices'}
              </button>
            )}
            <button
              type="button"
              onClick={() => setIsImportLotsOpen(true)}
              disabled={!auction.lots?.some(lot => lot.status === 'won')}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
              Import Lots
            </button>
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
              </svg>
              Edit
            </button>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 px-4 sm:px-6 pb-5">
          <div className="bg-gray-50 p-3 rounded-lg">
            <dt className="text-xs font-medium text-gray-500">Buyers Premium</dt>
            <dd className="mt-1 text-lg font-semibold text-gray-900">{Math.round(auction.buyers_premium || 0)}%</dd>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg">
            <dt className="text-xs font-medium text-gray-500">Profit Margin</dt>
            <dd className="mt-1 text-lg font-semibold text-gray-900">{Math.round(auction.profit_margin || 0)}%</dd>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg">
            <dt className="text-xs font-medium text-gray-500">Total Lots</dt>
            <dd className="mt-1 text-lg font-semibold text-gray-900">{auction.lots?.length || 0}</dd>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg">
            <dt className="text-xs font-medium text-gray-500">Won Lots</dt>
            <dd className="mt-1 text-lg font-semibold text-green-600">
              {auction.lots?.filter(lot => ['won', 'imported'].includes(lot.status)).length || 0}
            </dd>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg">
            <dt className="text-xs font-medium text-gray-500">Lost Lots</dt>
            <dd className="mt-1 text-lg font-semibold text-red-600">
              {auction.lots?.filter(lot => lot.status === 'lost').length || 0}
            </dd>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg">
            <dt className="text-xs font-medium text-gray-500">Total Bids</dt>
            <dd className="mt-1 text-lg font-semibold text-gray-900">
              {formatCurrency(
                auction.lots?.reduce((sum, lot) => sum + (['won', 'imported'].includes(lot.status) ? (lot.winning_bid || 0) : 0), 0) || 0,
                auction.auction_house_currency
              )}
            </dd>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg">
            <dt className="text-xs font-medium text-gray-500">Total Cost</dt>
            <dd className="mt-1 text-lg font-semibold text-gray-900">
              {formatCurrency(
                auction.lots?.reduce((sum, lot) => {
                  if (!['won', 'imported'].includes(lot.status) || !lot.winning_bid) {
                    return sum;
                  }
                  return sum + (lot.winning_bid * (1 + (auction.buyers_premium || 0) / 100));
                }, 0) || 0,
                auction.auction_house_currency
              )}
            </dd>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 mt-4">
          <TableProvider
            type="auction_lots"
            buyersPremium={auction.buyers_premium || 0}
            houseCurrency={auction.auction_house_currency}
            userCurrency={auction.user_currency}
            profitMargin={auction.profit_margin || 0}
            rate={rate}
            initialState={{
              loading: isLoading, 
              error: errorMessage
            }}
          >
        <TableContent
            auction={auction}
            processedLots={processedLots}
            isLoading={isLoading}
            error={error}
            onAddLots={() => setIsAddLotOpen(true)}
            handleUpdateLot={handleUpdateLot}
            navigate={navigate}
            onSystemAuctionAddLots={handleSystemAuctionAddLots}
          />
        </TableProvider>
      </div>

      <Transition.Root show={isAddLotOpen} as={React.Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => setIsAddLotOpen(false)}>
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 w-[95%] max-w-[1600px] sm:p-6">
                  <div className="flex justify-between items-center mb-4">
                    <Dialog.Title className="text-lg font-medium">Add Lots</Dialog.Title>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={batchForm.handleAddRow}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        Add Row
                      </button>
                      <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={isCreatingLot}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-400"
                      >
                        {isCreatingLot ? 'Adding...' : 'Add All'}
                      </button>
                    </div>
                  </div>

                  <div className="mt-2 max-w-xl text-sm mb-4">
                    <p className="text-gray-500">Enter lot details below or paste data from a spreadsheet. You can paste multiple rows at once.</p>
                    {formError && (
                      <p className="text-red-600 mt-2">{formError}</p>
                    )}
                  </div>

                  <BatchLotForm
                    rows={batchForm.rows}
                    selectedCells={batchForm.selectedCells}
                    isDragging={batchForm.isDragging}
                    onCellMouseDown={batchForm.handleCellMouseDown}
                    onCellMouseEnter={batchForm.handleCellMouseEnter}
                    onCellMouseUp={batchForm.handleCellMouseUp}
                    onPaste={batchForm.handlePaste}
                    onCellChange={batchForm.handleCellChange}
                    onKeyDown={batchForm.handleKeyDown}
                    onRemoveRow={batchForm.handleRemoveRow}
                    onFetchCertDetails={() => batchForm.handleFetchCertDetails(services)}
                    fetchProgress={batchForm.fetchProgress}
                    logs={batchForm.logs}
                  />
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Delete Confirmation Dialog */}
      <Transition.Root show={isDeleteOpen} as={React.Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => setIsDeleteOpen(false)}>
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                      <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                      </svg>
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                        Delete Auction
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Are you sure you want to delete this auction? This will also delete all lots associated with this auction. This action cannot be undone.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      disabled={isDeleting}
                      className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto disabled:bg-red-400"
                      onClick={handleDelete}
                    >
                      {isDeleting ? 'Deleting...' : 'Delete'}
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                      onClick={() => setIsDeleteOpen(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Delete Blocked Dialog */}
      <Transition.Root show={isDeleteBlockedOpen} as={React.Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => setIsDeleteBlockedOpen(false)}>
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                      <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                      </svg>
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                        Cannot Delete Auction
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          This auction cannot be deleted because it contains won or imported lots. Please remove or update the status of these lots before attempting to delete the auction.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                      onClick={() => setIsDeleteBlockedOpen(false)}
                    >
                      Close
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      <AuctionFormModal
        isOpen={isEditing}
        onClose={() => setIsEditing(false)}
        isEdit={true}
        initialData={auction}
        onDelete={() => setIsDeleteOpen(true)}
      />

      <LotFormModal
        isOpen={isEditLotOpen}
        onClose={() => {
          setIsEditLotOpen(false);
          setSelectedLot(null);
        }}
        lot={selectedLot}
        auctionId={auction.id}
      />
      
      <ImportLotsModal
        isOpen={isImportLotsOpen}
        onClose={() => setIsImportLotsOpen(false)}
        auctionId={auction.id}
        wonLotsCount={auction.lots?.filter(lot => lot.status === 'won').length || 0}
      />

      {/* Hammer Price Success Modal */}
      <Transition.Root show={isHammerPriceSuccessOpen} as={React.Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => setIsHammerPriceSuccessOpen(false)}>
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                  <div>
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                      <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                    </div>
                    <div className="mt-3 text-center sm:mt-5">
                      <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                        Hammer Prices Updated
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          {hammerPriceUpdateCount === 0 
                            ? 'No new hammer prices found.'
                            : `Successfully updated ${hammerPriceUpdateCount} lot${hammerPriceUpdateCount === 1 ? '' : 's'} with new hammer prices.`}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-6">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                      onClick={() => setIsHammerPriceSuccessOpen(false)}
                    >
                      Close
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

    </div>
  );
}
